import select from 'scoreboard-select'
import icon from 'scoreboard/components/icon'
import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import ScoreboardSifter from 'scoreboard/components/scoreboard-sifter'
import escapeStringRegexp from 'escape-string-regexp'
import * as d3 from 'd3-selection'
import arrayToHash from 'scoreboard/components/array-to-hash'
// The max number of results we display in a filtering dropdown menu #59228
const MAX_FILTERING_RESULTS = 1000

var debug = false

// define as a function b/c i18n won't be loaded right away
function defaults() {
	// do NOT set accessors here, because for normal HTML selects you need the
	// defaults of label and value

	function formatLabel(d) {
		var preLabel = _.result(d, 'dataset.pre-label') ? '<span class="pre-label">' + _.result(d, 'dataset.pre-label') + '</span>' : ''
		var postLabel = _.result(d, 'dataset.post-label') ? '<span class="post-label">' + _.result(d, 'dataset.post-label') + '</span>' : ''
		let selectIcon = ''
		if (_.result(d, 'dataset.icon')) {
			selectIcon = icon(_.result(d, 'dataset.icon'), 'selected-icon ' + _.result(d, 'dataset.icon'))
		} else if (d.icon) {
			selectIcon = icon(d.icon, 'selected-icon')
		}
		return `${selectIcon}<span>${preLabel}${d.label}${postLabel}</span>`
	}

	return {
		render: {
			option: formatLabel,
			item: formatLabel
		},
		placeholder: i18n.get('common.startTyping'),
		filterSuggestion: i18n.get('common.select.filterSuggestion'),
		fullHeight: true,
		escapeLabels: true
	}
}

/*
 * Returns a native array of the elements in `el`.
 * If `el` is already an array, we return the array. If it's a NodeList, turn it into
 * a native array.
 * If it's a jQuery object we unwrap it so it's a native array of DOM elements
 */
function toArray(el) {
	if (Array.isArray(el)) {
		// Already an array so we can return it as-is.
		return el
	}
	if (NodeList.prototype.isPrototypeOf(el)) {
		// el is a NodeList, turn it into a native array
		return Array.from(el)
	}
	return (el instanceof $) ? el.get() : [el]
}

/**
 * Convert the native select elements into rich selects
 * @param {JQuery|Element} els single select element or a jquery list of select elements
 * 		WARNING: If this is being called repeatedly the selector should be qualified
 * 		with `select.{selector}` otherwise scoreboard-select won't work correctly,
 * 		as it will re-select the rich select created initially.
 * @param [options] see scoreboard-select repo example for full documentation
 * @param {String|Number|Array} [options.selected] if specified, will make sure
 * the value(s) specified are selected
 * @param {Boolean} [options.escapeLabels=true] if set to false, labels won't
 * automatically be escaped
 */
function selects(els, options) {
	let rs = []
	// Loop over each one to dispatch the event, rather than using
	// `d3.selectAll` which doesn't seem to work how we'd like
	toArray(els).forEach(el => {
		// For the 'autofocus' property and the event binding with a non-ancestor <label>
		// to work, we need the select to actually be in the DOM when we run select()
		// However, don't add a process.nextTick here.  Instead, make sure your view
		// is in the DOM before selects are drawn
		var opts = _.defaults({}, options, defaults())

		// #52565 prevent the passed-in options from being mutated by the
		// scoreboard-select. See
		// https://github.com/SpiderStrategies/scoreboard-select/issues/372
		if (opts.options) {
			opts.options = createProtectedArray(opts.options)
		}

		// default is true
		if (opts.escapeLabels) {
			// escape all the options before they're displayed to avoid XSS vulnerabilities
			_.forEach(opts.options, opt => {
				opt.label = _.escape(opt.label)
				if (opt.linkedDataset) {
					opt.linkedDataset = _.escape(opt.linkedDataset)
				}
			})
		}

		let groupProp = opts.groupBy
		if (!_.isNil(groupProp)) {
			//#45511 #45593 only add grouping header if at least one of the options
			//is actually defined in a grouping
			let hasGrouping = _.some(opts.options, o => {
				return _.get(o, groupProp, 'headless') !=  'headless'
			})
			if (hasGrouping) {
				// provide a default groupHeader method, if one wasn't specified and it's needed
				if (_.isNil(opts.render.groupHeader)) {
					opts.render.groupHeader = _.escape
				}
				$(el).addClass('has-grouped-menu')
			}
		}

		// To prevent a select within a marked-field from being rendered twice,
		// we can't pass the autofocus into the select method. Instead, we will
		// store it in a variable and remove it from `opts`.
		var autofocus = opts.autofocus
		delete opts.autofocus

		addSelections(opts)

		if (debug) {
			console.dir(opts)
		}

		rs.push(select(el, opts))

		// handle recording of previousValue AFTER rendering the select.
		// Otherwise, there are situations where we might record the wrong
		// previousValue (e.g. when the selected options are specified
		// programmatically)
		let recordPrevious = () => {
			$(el).data('previousValue', $(el).val())
			// leaving this here for a bit b/c it's handy for debugging
			// console.log('previousValue: ' + $(el).val())
		}
		// try really hard to make sure other JS has a chance to execute before
		// updating the previousValue by using event capturing and
		// process.nextTick
		el.addEventListener('change', () => process.nextTick(recordPrevious), true)
		recordPrevious()

		if (autofocus) {
			toggleFocus(el, true)
		}
	})
	return rs
}

function createProtectedArray(array) {
	return array?.map(item => Object.create(item)) ?? []
}

function addSelections(options) {
	if (_.isNil(_.get(options, 'selected'))) return

	let valueAccessor = _.get(options, 'accessors.value') || 'value'
	let selected = // note strings are also considered arrays by lodash
		_.isArray(options.selected) && !_.isString(options.selected) ?
		options.selected : [options.selected]
	let selectedHash = arrayToHash(selected)
	_.each(options.options, option => {
		option.selected = !!selectedHash[option[valueAccessor]]
	})
}

/**
 * Returns the value just selected in the given select. For a select with
 * multiple: false this isn't useful b/c you can just call .val(). When
 * multiple: true, however, this lets you easily find the value that was just
 * added.
 * @param {jQuery} el select element
 */
function selected(el) {
	let current = $(el).val()
	if (_.isArray(current)) {
		let previous = previousValue(el)
		return _.first(_.difference(current, previous))
	} else {
		return current
	}
}


/**
 * Returns the previous value for the given select
 * @param {Element|JQuery|String} selectEl DOM element, jQuery object, or
 * Selector that denotes the select whose value should be changed
 */
function previousValue(selectEl) {
	return $(selectEl).data('previousValue')
}


/**
 * creates a filtering select, and applies a lexical sort to the options using
 * the label value.  Also contains the basics for a select-multiple
 * (e.g. tags-chooser) or a combo box (e.g. datasets record-form.js)
 *
 * @param {jQuery} el select element
 * 		WARNING: If this is being called repeatedly the selector should be qualified
 * 		with `select.{selector}` otherwise scoreboard-select won't work correctly,
 * 		as it will re-select the rich select created initially.
 *
 * @param {array} options.options The HTML options. option.label will be
 * escaped automatically unless you pass in escapeLabels: false
 * Supported Option Attributes:
 * @param {String} [options.option.group]
 * 		supported values:
 *   	'headerless' - floats the option to the top of the list
 *
 * @param {Boolean} [options.escapeLabels=true] if set to false, labels won't
 * automatically be escaped
 *
 * @param {boolean} [options.multiple=false] Multi-select (as tags)
 *
 * @param {array} [options.sort=[{field: 'label', direction: 'asc'}]]
 * 	Pass an empty array to disable sifter sorting and maintain ordinal positioning
 *
 * @param {Function} [options.filter=this.siftFunction] Override (or disable) the
 * default filter (sifting) functionality (see global-search.js#draw)
 *
 * @param {Function} [options.fieldAdapters] Hash of fieldLabel:adapterFunction
 * The adapter function will be invoked with the value of the field before any
 * sort/score calculation is performed.
 * (e.g. removes a character; see vi-mapping.js)
 *
 * @param {Object} [options.render] To override option rendering. Provide an
 * object with one or more of the following:
 * {
 * 	 option: (data, searchTerm) => {}
 * 	 itemBefore: data => {}
 * 	 item: (d, input) => {}
 * 	 create: data => {}
 * 	 errorMessage: data => {}
 * }
 * see https://github.com/SpiderStrategies/scoreboard-select/blob/master/example/index.html for examples
 * along with usage in this project.
 *
 * NOTE: If one of the options needs to be (pre)selected do not pass it's value
 * in the filtering options.  First invoke selects.filtering() and then call
 * selects.val(select, preSelectedValue)
 * https://github.com/SpiderStrategies/Scoreboard/issues/34940#issuecomment-795607245
 */
function filtering(el, options) {
	el = $(el)
	var opts = _.assignIn({}, options, {
		render: _.extend({
			option: (d, searchTerm) => optionHtml(d, searchTerm, options)
		}, _.get(options, 'render')),
		filter: options.filter || siftFunction(options, el)
	})
	// make sure if a create function was specified, it makes it into the final
	// opts.  This is clumsy, but I couldn't figure out a more elegant solution.
	// I tried _.defaultsDeep with no luck
	var create = _.get(options, 'render.create')
	if (create) {
		opts.render.create = create
		// this seems pretty obvious, but scoreboard-select wants to see it.
		// See discussion in https://github.com/SpiderStrategies/scoreboard-select/issues/210
		if (_.isNil(opts.creatable)) {
			opts.creatable = true
		}
	}

	if (options.options?.length > MAX_FILTERING_RESULTS) {
		// #59228 need to limit the options when over MAX_FILTERING_RESULTS
		// for performance purposes
		opts.maxFilteringResults = MAX_FILTERING_RESULTS
	}
	return selects(el, opts)
}

/**
 * Applies the sifter and sorting rules to the options
 */
function siftFunction(options) {
	const {sort, accessors, fieldAdapters = {}, searchTerm} = options

	const noSort = sort && _.isEmpty(sort)
	let getScoreFunc = _.constant(undefined)
	if (noSort) {
		const labelAdapter = fieldAdapters['label'] || _.identity
		const size = _.size(options.options)
		_.each(options.options, (o, i) => o.rank = size-i)
		getScoreFunc = searchString => {
			return item => {
				// sifter excludes items with score <= 0
				const label = labelAdapter(item.label, item)
				return _.includes(label.toLowerCase(), searchString.toLowerCase()) ? item.rank : 0
			}
		}
	}
	let searchFields = accessors ? _.values(accessors) : []
	if (!!searchTerm) searchFields.push(searchTerm)
	if (_.isEmpty(searchFields)) searchFields = ['label', 'value']

	// the scoreboard-select will just assume the label if that isn't specified
	// so we definitely want to search based on that when using the sifter
	// in the filter method
	// a single searchTerm can be specified in certain cases, i.e. the message
	// code hasn't been resolved, so don't use the "label" in those instances
	if (_.indexOf(searchFields, 'label') === -1 && !searchTerm) {
		// #32639 copy the labels because they are escaped once they're attached to the DOM
		// which will end up being fed back through the sift function and we will be searching
		// on escaped characters. e.g. a field with '<' in it will come up for a search of '&lt;'
		options.options.forEach(opt => opt.searchLabel = opt.label)
		searchFields.push('searchLabel')
	}

	let sortFields = options.sortFields ?
		options.sortFields :
		_.get(options, 'sort', [{field: 'label', direction: 'asc'}])
	if (!_.isNil(sortFields) && !_.isArray(sortFields)) {
		sortFields = [sortFields]
	}

	// #24544 sort the options before sending them to scoreboard-select, because
	// sifter won't be called unless text has been entered
	ScoreboardSifter.sort(options.options, sortFields)

	// #52789 important not to create the Sifter yet, because the selects()
	// function uses createProtectedArray() to create a copy of options.options.
	// Instead, we need to wait until Sifter is actually called by scoreboard-select
	// so that it is using the correct array of options ("optionsToSift" below)
	let sifter

	return (value, optionsToSift) => {
		if (!optionsToSift) {
			// #52817 if we're using siftFunction() independently of the scoreboard-select,
			// we might not be passing in a new set of options to sift. In that case, just
			// use the original ones
			optionsToSift = options.options ?? []
		}
		if (!sifter) {
			sifter = new ScoreboardSifter(optionsToSift, {
				fieldAdapters
			})
		}
		const sifterOptions = {
			fields: searchFields,
			conjunction: 'and',
			sort: sortFields,
			score: getScoreFunc(value)
		}
		const sifted = sifter.search(value, sifterOptions)

		const selectOptions = sifted.items.map(item => {
			let option = optionsToSift[item.id]
			return option
		})
		return applyGlobalSortRules(selectOptions)
	}
}

/**
 * Rules:
 * If there is a headerless group, it should always show at the top of the list
 *
 * @param {Array} selectOptions the options to sort
 * @return {Array} a new sorted array of options
 */
function applyGlobalSortRules(selectOptions) {
	return _.sortBy(selectOptions, (o) => o.group === 'headerless' || _.isNil(o.group) ? -1 : 1)
}

// focus the given select
function toggleFocus(selects, focus) {
	// doesn't work without process.nextTick for some reason
	process.nextTick(function() {
		let textarea = el(selects).first().find('textarea')
		if (focus) {
			textarea.focus()
			textarea?.[0]?.dispatchEvent(new Event('focus'))
		} else {
			textarea.blur()
			textarea?.[0]?.dispatchEvent(new Event('blur'))
		}
	})
}

// given the element for a native <select>, returns the root dom element that
// creates the appearance of a dropdown for users to see
function el(selectEl) {
	return $(selectEl).next()
}

/**
 * Update the select's **value** without triggering a change event
 * To provide new options, call `convertSelects` or `filtering` again.
 *
 * WARNING: this mutates the options that were originally passed into the
 * select. See explanation here:
 * https://github.com/SpiderStrategies/scoreboard-select/issues/372
 *
 * Example 1:
 * selectEl.val('something-new')
 * selects.update(selectEL)
 *
 * Example 2:
 * selects.update(selectEl, {detail: {value: 'something-new'}})
 *
 * @param {JQuery|HTMLElement|HTMLElement[]} selectEl
 * @param {Object} [options] d3 dispatch options
 * @param {Object} options.detail the details of what should be changed
 */
function update(selectEl, options) {
	toArray(selectEl).forEach(select => {
		// Loop over each one to dispatch the event, rather than using `d3.selectAll` which seems to dispatch
		// the events on all the select's children (i.e. `option`) which isn't what we want.
		d3.select(select).dispatch('change:rich', options)
	})
}

// returns true if the given element is part of a rich select
function isRich(el) {
	el = $(el)
	if (el.hasClass('select') ||
		el.parent('.select-item') || //textarea (main control) of the rich select
		el.next().hasClass('select')) {
		return true
	}
	return false
}

/**
 * Returns the option value with searchTerm highlighting applied
 * @param {Object} optionData
 * @param {String} optionData.label
 * @param {String} searchTerm
 * @param {String} [options.labelProperty='label'] the optionData property to use for the label
 * @param {String} [options.cssClasses] the optionData property to use for the label
 * @param {String} [options.termRegexFn] Must return a RegExp object used highlight the search term
 * @param {Boolean} [options.escapeLabels=true] if set to false, labels won't automatically be escaped
 */
function optionHtml(optionData, searchTerm, options = {}) {
	// always escape the labels, unless the caller has already escaped them
	options = _.extend({}, {
		escapeLabels: true
	}, options)

	const {
		cssClasses = '',
		labelProperty = 'label',
		// escape potential HTML in the searchTerm so special characters are highlighted properly
		termRegexFn = term => new RegExp(escapeStringRegexp(_.escape(term)), 'i')
	} = options

	let label = _.get(optionData, labelProperty)
	// #42276 check for HTML content (e.g. dataset's "No Primary Key" option)
	// because we must not match on the html tag(s), only the label
	try {
		if (options.escapeLabels) {
			label = _.escape(label)
		}

		// have to provide a tag instead of creating an empty jQuery object, otherwise
		// .html(label) won't have anything to append the html to
		const tempEl = $('<div>')
		// append the html to our temporary element, and then get the innerText
		label = tempEl.html(label).text()
	} catch (ignored) {}

	let customIcon = optionData.customIcon ? icon(optionData.customIcon) : ''
	if (searchTerm) {
		var regExp = termRegexFn(searchTerm)
		// the labels are already escaped at this point and don't need to be escaped again
		return `<span class="search-no-match ${cssClasses}">` +
			label.replace(regExp, v => `<span class="search-match">${v}</span>`) +
		'</span>'
	} else {
		return `${customIcon}<span class="${cssClasses}">${label || ''}</span>`
	}
}

/**
 * Sets or returns the value for a select
 * @param {Element|JQuery|String} selectEl DOM element, jQuery object, or
 * Selector that denotes the select whose value should be changed. Use of a
 * String selector is NOT recommend because it will be scoped to the entire
 * page instead of to the component you're working with
 * @param [value] if specified, the selects value will be changed to this
 * select's value
 * @returns the current value for the select, if "value" wasn't specified
 */
function val(selectEl, value) {
	selectEl = $(selectEl)
	if (_.isUndefined(value)) {
		return selectEl.val()
	} else {
		selectEl.val(value)
		update(selectEl, { detail: { value } })
	}
}

/**
 * Sorts options by their label (in place)
 *
 * Optionally, apply a fixed grouping
 *
 * @param {String} [options.groupBy] property on the select options that should be used to
 *        group them; nil means the options will not be grouped
 *
 * @param {String[]} [options.groupSortOrder] If a `groupBy` is provided then
 * scoreboard-select aborts it's sort logic (https://github.com/SpiderStrategies/scoreboard-select/blob/f6f82f8c7f55d514110423aee72429d966b7760f/lib/menu-options.js#L35).
 * If the groups need to be in a fixed order, you must provide them in
 * an ordered array here where each value is `option[groupSortAttribute]`
 * (e.g. ['user', 'group', ...])
 *
 * @param {String} [options.groupSortAttribute=type] When specifying groupSortOrder
 * the option attribute to actually sort by.  Default is type (e.g. 'user', 'group', etc.)
 *
 * @param selectOptions
 */
function sortOptions(options = {}, selectOptions) {
	const {groupBy, groupSortOrder, groupSortAttribute = 'type'} = options

	let groupSortPosition = {}
	_.each(groupSortOrder, (s, i) => groupSortPosition[s] = i + 1)

	const groupPosFn = option => {
		const sortValue = option[groupSortAttribute]
		return sortValue ? groupSortPosition[sortValue] : 0
	}
	selectOptions.sort((o1, o2) => {
		// all the negative IDs are special groups like "Everyone" and
		// "All Power Users".  They should appear before the other groups,
		// and in order of ID (-1 first, -40 last)
		if (o1.id < 0 && o2.id < 0) {
			return o2.id - o1.id
		} else if (o1.id < 0) {
			return -1
		} else if (o2.id < 0) {
			return 1
		} else {
			let groupSort = 0
			if (groupBy && groupSortOrder) {
				groupSort = groupPosFn(o1) - groupPosFn(o2)
			}
			return groupSort === 0 ? o1.label.localeCompare(o2.label) : groupSort
		}
	})

	return selectOptions
}


selects.toggleFocus = toggleFocus
selects.el = el
selects.filtering = filtering
selects.update = update
selects.isRich = isRich
selects.optionHtml = optionHtml
selects.selected = selected
selects.val = val
selects.previousValue = previousValue
selects.defaults = defaults
selects.sortOptions = sortOptions
selects.siftFunction = siftFunction

// For testing only
selects.applyGlobalSortRules = applyGlobalSortRules
selects.createProtectedArray = createProtectedArray

export default selects
selects.__filename__ = 'components/selects.js'
